import { useState, useRef, useEffect } from 'react';

interface TrendingTopic {
  id: string;
  topic: string;
  category: string;
  searches: string;
  trendPct: number;
  isHot: boolean;
  icon: string;
  color: string;
  sparkline: number[];
}

const TRENDING: TrendingTopic[] = [
  { id: 'p1', topic: 'AI & Machine Learning Trends', category: 'Tech', searches: '22.1K', trendPct: 248, isHot: true, icon: 'ri-robot-line', color: '#8b5cf6', sparkline: [20, 30, 42, 58, 70, 88, 100] },
  { id: 'p2', topic: 'Startup Fundraising Deck', category: 'Startup', searches: '17.4K', trendPct: 192, isHot: true, icon: 'ri-rocket-line', color: '#f97316', sparkline: [25, 34, 46, 60, 74, 86, 100] },
  { id: 'p3', topic: 'Climate Change Report', category: 'Science', searches: '15.8K', trendPct: 178, isHot: true, icon: 'ri-leaf-line', color: '#10b981', sparkline: [28, 38, 50, 64, 76, 88, 100] },
  { id: 'p4', topic: 'Product Launch Strategy', category: 'Marketing', searches: '14.2K', trendPct: 156, isHot: false, icon: 'ri-megaphone-line', color: '#ec4899', sparkline: [32, 42, 54, 66, 78, 89, 100] },
  { id: 'p5', topic: 'Annual Business Review', category: 'Business', searches: '12.9K', trendPct: 143, isHot: false, icon: 'ri-bar-chart-box-line', color: '#0ea5e9', sparkline: [35, 44, 56, 68, 79, 90, 100] },
  { id: 'p6', topic: 'Healthcare Innovation', category: 'Medical', searches: '11.6K', trendPct: 129, isHot: false, icon: 'ri-heart-pulse-line', color: '#ef4444', sparkline: [38, 47, 58, 70, 80, 90, 100] },
  { id: 'p7', topic: 'Digital Transformation', category: 'Tech', searches: '10.8K', trendPct: 118, isHot: false, icon: 'ri-git-branch-line', color: '#6366f1', sparkline: [40, 50, 60, 71, 81, 91, 100] },
  { id: 'p8', topic: 'Remote Work Culture', category: 'HR', searches: '9.7K', trendPct: 107, isHot: false, icon: 'ri-home-office-line', color: '#d97706', sparkline: [42, 52, 62, 73, 82, 91, 100] },
  { id: 'p9', topic: 'Financial Forecasting 2025', category: 'Finance', searches: '9.1K', trendPct: 96, isHot: false, icon: 'ri-funds-line', color: '#0891b2', sparkline: [45, 54, 64, 74, 83, 92, 100] },
  { id: 'p10', topic: 'Sustainable Business', category: 'Non-Profit', searches: '8.4K', trendPct: 88, isHot: false, icon: 'ri-recycle-line', color: '#22c55e', sparkline: [48, 57, 66, 76, 84, 92, 100] },
  { id: 'p11', topic: 'Real Estate Investment', category: 'Real Estate', searches: '7.9K', trendPct: 79, isHot: false, icon: 'ri-building-line', color: '#be185d', sparkline: [50, 59, 68, 77, 85, 93, 100] },
  { id: 'p12', topic: 'Brand Refresh Campaign', category: 'Marketing', searches: '7.3K', trendPct: 71, isHot: false, icon: 'ri-palette-line', color: '#7c3aed', sparkline: [52, 61, 70, 79, 86, 93, 100] },
];

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const H = 24;
  const W = 48;
  const step = W / (data.length - 1);
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => ({ x: i * step, y: H - ((v - min) / range) * H }));
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const fill = d + ` L ${(data.length - 1) * step} ${H} L 0 ${H} Z`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="flex-shrink-0">
      <path d={fill} fill={color} fillOpacity="0.15" />
      <path d={d} stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r="2.5" fill={color} />
    </svg>
  );
}

function TopicCard({
  topic, rank, onSearch, onCreate,
}: {
  topic: TrendingTopic; rank: number;
  onSearch: () => void; onCreate: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const heat = topic.trendPct >= 200 ? '#ef4444' : topic.trendPct >= 150 ? '#f97316' : topic.trendPct >= 100 ? '#f59e0b' : '#10b981';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex-shrink-0 w-64 bg-white border-2 rounded-2xl p-4 transition-all duration-300 cursor-default"
      style={{
        borderColor: hovered ? topic.color : '#e2e8f0',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white flex-shrink-0 transition-transform duration-300"
            style={{ backgroundColor: topic.color, transform: hovered ? 'rotate(8deg) scale(1.1)' : 'none' }}
          >
            <i className={`${topic.icon} text-base`}></i>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-black text-slate-400">#{rank}</span>
              {topic.isHot && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-50 border border-red-200 rounded-full text-xs font-bold text-red-600">
                  <i className="ri-fire-fill text-xs"></i> HOT
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{topic.category}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-black" style={{ backgroundColor: heat + '15', color: heat }}>
            <i className="ri-arrow-up-line text-xs"></i>+{topic.trendPct}%
          </div>
          <span className="text-xs text-slate-400 font-semibold">{topic.searches}</span>
        </div>
      </div>
      <p className="text-sm font-extrabold leading-snug mb-3 transition-colors" style={{ color: hovered ? topic.color : '#0f172a' }}>
        {topic.topic}
      </p>
      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-xs text-slate-400 mb-1">7-day trend</p>
          <Sparkline data={topic.sparkline} color={topic.color} />
        </div>
        <span className="text-xs text-slate-400">{topic.searches} searches</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <button
          onClick={onSearch}
          className="flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer whitespace-nowrap"
          style={{
            borderColor: topic.color + '50',
            backgroundColor: hovered ? topic.color + '12' : '#f8fafc',
            color: hovered ? topic.color : '#475569',
          }}
        >
          <i className="ri-search-line text-xs"></i>Templates
        </button>
        <button
          onClick={onCreate}
          className="flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-bold text-white transition-all cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: topic.color }}
        >
          <i className="ri-magic-line text-xs"></i>Create
        </button>
      </div>
    </div>
  );
}

interface Props {
  onTopicSearch: (topic: string) => void;
  onCreateFromTopic: (topic: string) => void;
}

export default function PresentationTrendingTopics({ onTopicSearch, onCreateFromTopic }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [liveCount, setLiveCount] = useState(3841);

  useEffect(() => {
    const id = setInterval(() => setLiveCount(c => c + Math.floor(Math.random() * 4)), 3000);
    return () => clearInterval(id);
  }, []);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 20);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 20);
  };

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'right' ? 560 : -560, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener('scroll', checkScroll); checkScroll(); }
    return () => { el?.removeEventListener('scroll', checkScroll); };
  }, []);

  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="flex items-center gap-1.5 px-3 py-1 bg-rose-50 border border-rose-200 rounded-full">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></div>
                <span className="text-xs font-extrabold text-rose-600 uppercase tracking-wider">Live</span>
              </div>
              <h2 className="text-xl font-extrabold text-slate-900">Trending Presentation Topics</h2>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 px-2.5 py-1 bg-slate-100 rounded-full">
                <i className="ri-refresh-line text-slate-400"></i>Updated every 10 min
              </span>
            </div>
            <p className="text-sm text-slate-500">What creators are building right now — click any topic to find or create matching slides</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 border border-rose-200 rounded-2xl flex-shrink-0">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-presentation-line text-rose-500 text-lg"></i>
            </div>
            <div>
              <p className="text-base font-extrabold text-rose-700 tabular-nums">{liveCount.toLocaleString()}</p>
              <p className="text-xs text-rose-500">building now</p>
            </div>
          </div>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 flex items-center pointer-events-none">
              <button onClick={() => scroll('left')} className="ml-1 w-9 h-9 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 pointer-events-auto">
                <i className="ri-arrow-left-s-line text-slate-600"></i>
              </button>
            </div>
          )}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 flex items-center justify-end pointer-events-none">
              <button onClick={() => scroll('right')} className="mr-1 w-9 h-9 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 pointer-events-auto">
                <i className="ri-arrow-right-s-line text-slate-600"></i>
              </button>
            </div>
          )}
          <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {TRENDING.map((topic, i) => (
              <TopicCard
                key={topic.id} topic={topic} rank={i + 1}
                onSearch={() => onTopicSearch(topic.topic)}
                onCreate={() => onCreateFromTopic(topic.topic)}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            {['🔥 Hottest', '📈 Fastest growing', '💡 Hidden gems'].map(t => (
              <span key={t} className="text-xs text-slate-400 font-medium">{t}</span>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <i className="ri-arrow-left-right-line text-slate-300"></i>
            Scroll to see all {TRENDING.length} trending topics
          </div>
        </div>
      </div>
    </div>
  );
}
